import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCol,
  IonGrid,
  IonIcon,
  IonListHeader,
  IonRow,
  IonText,
} from '@ionic/react';
import { closeCircleOutline, informationCircleOutline } from 'ionicons/icons';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useMeasure } from 'react-use';
import {
  ChartTemplate,
  useListChartTemplatesQuery,
} from '../../api/graphql/generated';
import { trackInitiateChartSuggestion } from '../../config/analytics';
import { ApplicationConfig } from '../../config/config';
import { FeedbackLink } from '../feedback/FeedbackLink';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { FlipCard } from '../ui/flip-card';
import { orderChartTemplates } from './chart-utils';
import './chart.css';

interface ChartTemplatesProps {
  parentObject: 'team' | 'tenant' | 'machine';
  parentId: string;
  routerBuilder: (templateId: string) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  historyState?: () => any;
}

interface CardFlipIconProps {
  onClick?: () => void;
  icon: string;
}

interface ChartTemplateSuggestionProps {
  height?: number;
}

const ChartTemplateGallery = (props: ChartTemplatesProps): ReactElement => {
  const { parentObject, routerBuilder, historyState: routerState } = props;
  const { i18n } = useTranslation();

  const [ref, { height }] = useMeasure<HTMLDivElement>();

  const { data, loading, error, refetch } = useListChartTemplatesQuery({
    variables: {
      input: {
        parentObject,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const templates: ChartTemplate[] = orderChartTemplates(
    data?.listChartTemplates.items
  );

  /* eslint-disable */
  const getHistoryState = routerState ?? (() => {});

  return (
    <LoadingRetry
      loading={loading}
      error={error}
      results={{
        hasResults: templates.length !== 0,
        noResultsComponent: (
          <div className="full-height-width content-center">
            <IonText className="ion-padding ion-text-center" color="medium">
              {i18n.t('common:chart_no_templates_available')}
            </IonText>
          </div>
        ),
      }}
      refetch={refetch}
    >
      <IonGrid>
        <IonRow>
          {templates.map((template, index) => {
            return (
              <IonCol key={index} sizeXs={'12'} sizeLg={'6'} sizeXl={'4'}>
                <div ref={index === 0 ? ref : undefined}>
                  <ChartTemplateItem
                    {...template}
                    routerLink={routerBuilder(template.id)}
                    historyState={getHistoryState()}
                  />
                </div>
              </IonCol>
            );
          })}
          <IonCol sizeXs={'12'} sizeLg={'6'} sizeXl={'4'}>
            <ChartTemplateSuggestion height={height} />
          </IonCol>
        </IonRow>
      </IonGrid>
    </LoadingRetry>
  );
};

const ChartTemplateItem = (
  props: Partial<ChartTemplate> & { routerLink?: string; historyState?: any }
): ReactElement => {
  const { push } = useHistory();
  const { i18n } = useTranslation();
  const { name, description, image, routerLink, historyState } = props;

  const [flipped, set] = useState<boolean>(false);

  const chartImage =
    image && image.length > 0
      ? image
      : '/assets/charts/default-chart-template-preview.png';

  const onClick = () => {
    if (routerLink) {
      const parts = routerLink.split('?');
      push({
        pathname: parts[0],
        search: parts[1],
        state: historyState,
      });
    }
  };

  return (
    <FlipCard showBack={flipped}>
      <IonCard onClick={onClick}>
        <IonListHeader>
          <CardFlipIcon
            icon={informationCircleOutline}
            onClick={() => set(!flipped)}
          />
        </IonListHeader>
        <img className="template-preview" alt={`${name}`} src={chartImage} />
        <IonCardHeader>
          <IonCardTitle>{name}</IonCardTitle>
        </IonCardHeader>
      </IonCard>
      <IonCard style={{ height: '100%' }}>
        <IonListHeader>
          {i18n.t('common:chart_description')}
          <CardFlipIcon
            icon={closeCircleOutline}
            onClick={() => set(!flipped)}
          />
        </IonListHeader>
        <IonCardContent
          style={{
            height: '75%',
            overflow: 'auto',
          }}
        >
          <IonText className="chart-template-description-text">
            {description}
          </IonText>
        </IonCardContent>
      </IonCard>
    </FlipCard>
  );
};

const CardFlipIcon = (props: CardFlipIconProps): ReactElement => {
  const { icon, onClick } = props;

  // @ts-ignore
  const [ref, { height, width }] = useMeasure<HTMLIonIconElement>();

  return (
    <div
      style={{
        position: 'absolute',
        height,
        width,
        right: 12,
        zIndex: 10,
      }}
      onClick={(e) => {
        if (onClick) {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }
      }}
    >
      <IonIcon
        ref={ref}
        slot="end"
        icon={icon}
        color={'medium'}
        size={'large'}
      />
    </div>
  );
};

const ChartTemplateSuggestion = (
  props: ChartTemplateSuggestionProps
): ReactElement => {
  const { height } = props;
  const { i18n } = useTranslation();

  let computedHeight = 200;
  if (height) {
    computedHeight = height;
  }

  const href = `mailto:${ApplicationConfig.support.email}?subject=Beacon Chart Suggestion`;

  return (
    <IonCard
      className="chart-template-suggestion"
      style={{ height: computedHeight, display: 'grid', placeItems: 'center' }}
      href={href}
      onClick={(e) => {
        e.preventDefault();
        trackInitiateChartSuggestion();
        window.open(href);
      }}
    >
      <IonCardContent className="ion-text-center">
        <IonText className="ion-padding ion-text-center" color="medium">
          {i18n.t('common:chart_need_something_else')}
        </IonText>
        <br />
        <IonText className="ion-padding ion-text-center" color="medium">
          {i18n.t('common:contact_us')}
          <FeedbackLink className="chart-template-feedback-icon" size="20" />
        </IonText>
      </IonCardContent>
    </IonCard>
  );
};

export { ChartTemplateGallery };
